import React, { useEffect, useRef, useState } from "react";
import { Modal } from "../../../components/tailwind/modal/Modal";
import { ProductId } from "../../product";
import { GptGenerationTaskQueue } from "../../../api/gptApi";
import { Sentence } from "../types";
import { GeneratedResultCompactRow } from "./GeneratedResultCompactRow";
import { GeneratedSingleResult } from "./GeneratedSingleResult";
import { STREAMING_UNKNOWN_SECTION_NAME_HEADER } from "./GeneratedSingleResultHeader";

export const compactListTestId = "GeneratedResultsCompactList";
export const compactListLoadingTestId = "GeneratedResultsCompactList-loading";
export const compactListModalTestId = "GeneratedResultsCompactList-modal";
export const compactListCloseTestId = "GeneratedResultsCompactList-close";

type Props = {
  orderSentenceListBySectionName: { [sectionName: string]: Sentence[] };
  amountOfNewSentencesLoading: number;
  gptGenerationTaskQueue: GptGenerationTaskQueue;
  productId: ProductId;
  setActiveSection: (sentence: Sentence) => void;
  handleRemoveText: (sentence: Sentence) => Promise<void>;
  reFetchPrompts: () => Promise<void>;
};

export const GeneratedResultsCompactList: React.FC<Props> = ({
  orderSentenceListBySectionName,
  amountOfNewSentencesLoading,
  gptGenerationTaskQueue,
  productId,
  setActiveSection,
  handleRemoveText,
  reFetchPrompts,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [openSection, setOpenSection] = useState<string | null>(null);

  // Collapse all in-flight generations into a single "Generating…" row:
  // non-streaming pending sentences contribute via amountOfNewSentencesLoading,
  // streaming-without-section contributes via the synthetic header bucket.
  const hasInFlight =
    amountOfNewSentencesLoading > 0 ||
    Object.prototype.hasOwnProperty.call(
      orderSentenceListBySectionName,
      STREAMING_UNKNOWN_SECTION_NAME_HEADER
    );

  const rowEntries = Object.entries(orderSentenceListBySectionName).filter(
    ([sectionName]) => sectionName !== STREAMING_UNKNOWN_SECTION_NAME_HEADER
  );

  useEffect(() => {
    if (openSection !== null) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [openSection]);

  // Close the modal if the open section disappears from the data
  // (e.g. after delete, or after the underlying list refresh).
  useEffect(() => {
    if (openSection !== null && !orderSentenceListBySectionName[openSection]) {
      setOpenSection(null);
    }
  }, [openSection, orderSentenceListBySectionName]);

  const handleClose = (): void => {
    setOpenSection(null);
  };

  const handleRemoveTextFromModal = async (
    sentence: Sentence
  ): Promise<void> => {
    setOpenSection(null);
    await handleRemoveText(sentence);
  };

  const openSentences = openSection
    ? orderSentenceListBySectionName[openSection]
    : null;

  return (
    <div data-testid={compactListTestId} className="tw-flex tw-flex-col">
      {hasInFlight && (
        <div
          data-testid={compactListLoadingTestId}
          className="tw-flex tw-items-center tw-gap-3 tw-border-b tw-border-b-gray-200 tw-px-3 tw-py-2"
        >
          <span className="tw-inline-block tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-gray-300 tw-border-t-primary" />
          <span className="tw-text-sm tw-text-gray-600">Generating…</span>
        </div>
      )}

      {rowEntries.map(([sectionName, sentences]) => (
        <GeneratedResultCompactRow
          key={sectionName}
          sectionName={sectionName}
          sentences={sentences}
          setActiveSection={setActiveSection}
          onOpen={(): void => setOpenSection(sectionName)}
        />
      ))}

      <div data-testid={compactListModalTestId}>
        <Modal dialogRef={dialogRef} size="lg">
          <Modal.Body>
            {openSection && openSentences && (
              <GeneratedSingleResult
                gptGenerationTaskQueue={gptGenerationTaskQueue}
                sectionName={openSection}
                sentences={openSentences}
                productId={productId}
                handleRemoveText={handleRemoveTextFromModal}
                setActiveSection={setActiveSection}
                reFetchPrompts={reFetchPrompts}
              />
            )}
          </Modal.Body>
          <Modal.Actions>
            <button
              type="button"
              className="txu-btn txu-secondary-alt"
              onClick={handleClose}
              data-testid={compactListCloseTestId}
            >
              Close
            </button>
          </Modal.Actions>
        </Modal>
      </div>
    </div>
  );
};
